import { useState } from 'react';
import type { BucketListEntry, SmartFillBucket } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { DeleteBucketConfirmation } from './DeleteBucketConfirmation';
import { TrashIcon } from './Icons';
import { InlineError } from './SidebarState';
import { useActivateBucket, useDeleteBucket } from '../sidebar/data';
import { useSidebarHistory } from '../sidebar/history';
import { bucketPath } from '../sidebar/routes';
import { usePaste } from '../sidebar/paste';
import { bucketInitial, formatRelativeTime, pluralize } from '../sidebar/presentation';
import { countDocumentProperties } from '../buckets/document';

/** Shared list actions for all buckets and workspace children. Selecting never starts a fill. */
export function BucketRows({
  items,
  onChanged
}: {
  items: BucketListEntry[];
  onChanged: () => void;
}) {
  const { snapshot } = useSidebarSession();
  const activate = useActivateBucket();
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const deletion = useDeleteBucket();

  const removeBucket = async (bucket: SmartFillBucket) => {
    if (await deletion.remove(bucket.id)) {
      setConfirmingDelete(null);
      onChanged();
    }
  };

  return (
    <>
      {activate.error && <InlineError message={activate.error.message} />}
      {deletion.error && <InlineError message={deletion.error.message} />}
      <div className="bucket-list">
        {items.map((bucket) =>
          'unavailable' in bucket ? (
            <article
              key={bucket.id}
              className="bucket-row"
              aria-label={`${bucket.name} unavailable`}
            >
              <strong>{bucket.name}</strong>
              <p>This bucket cannot be opened right now. Contact Cara support.</p>
              <details>
                <summary>Support details</summary>
                <p>Bucket reference: {bucket.id}</p>
              </details>
            </article>
          ) : (
            <BucketRow
              key={bucket.id}
              bucket={bucket}
              active={snapshot.activeBucket?.bucketId === bucket.id}
              disabled={activate.busy}
              onActivate={() => void activate.activate(bucket.id)}
              confirmingDelete={confirmingDelete === bucket.id}
              deleting={deletion.deletingId === bucket.id}
              onRequestDelete={() => setConfirmingDelete(bucket.id)}
              onCancelDelete={() => setConfirmingDelete(null)}
              onDelete={() => void removeBucket(bucket)}
            />
          )
        )}
      </div>
    </>
  );
}

function BucketRow({
  bucket,
  active,
  disabled,
  onActivate,
  confirmingDelete,
  deleting,
  onRequestDelete,
  onCancelDelete,
  onDelete
}: {
  bucket: SmartFillBucket;
  active: boolean;
  disabled: boolean;
  onActivate: () => void;
  confirmingDelete: boolean;
  deleting: boolean;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
}) {
  const history = useSidebarHistory();
  const paste = usePaste();
  const count = countDocumentProperties(bucket.document);

  return (
    <article className={active ? 'bucket-row is-active' : 'bucket-row'}>
      {bucket.canEdit && !confirmingDelete && (
        <button
          type="button"
          className="bucket-row__delete"
          aria-label={`Delete ${bucket.name}`}
          disabled={deleting}
          onClick={onRequestDelete}
        >
          <TrashIcon />
        </button>
      )}
      <button type="button" className="bucket-row__header" disabled={disabled} onClick={onActivate}>
        <span className="bucket-avatar">{bucketInitial(bucket.name)}</span>
        <span className="bucket-row__copy">
          <strong>{bucket.name}</strong>
          <span>
            {count} {pluralize(count, 'field')} · updated {formatRelativeTime(bucket.updatedAt)}
          </span>
        </span>
        {active ? (
          <span className="active-pill">
            <span />
            Active
          </span>
        ) : (
          <span className="use-label">Use</span>
        )}
      </button>
      {confirmingDelete && (
        <DeleteBucketConfirmation
          name={bucket.name}
          busy={deleting}
          className="bucket-row__delete-confirmation"
          onCancel={onCancelDelete}
          onConfirm={onDelete}
        />
      )}
      {active && !confirmingDelete && (
        <div className="bucket-row__actions">
          <button
            type="button"
            className="button button--secondary button--pill"
            onClick={() => history.go(bucketPath(bucket.id))}
          >
            Open &amp; edit
          </button>
          <button
            type="button"
            className="button button--primary button--pill"
            disabled={!paste.canPaste || paste.continuous}
            onClick={() => void paste.pasteOnce()}
          >
            Fill page
          </button>
        </div>
      )}
    </article>
  );
}
